import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { platformSaasSubscriptionsDashboardService } from '../../services/platformSaasSubscriptionsDashboardService';

const { Title, Text } = Typography;

const SUBSCRIPTION_STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'TRIAL', value: 'TRIAL' },
  { label: 'SUSPENDED', value: 'SUSPENDED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
  { label: 'GRACE', value: 'GRACE' },
  { label: 'Sem assinatura', value: 'NO_SUBSCRIPTION' },
];

const RISK_OPTIONS = [
  { label: 'Baixo', value: 'low' },
  { label: 'Atencao', value: 'attention' },
  { label: 'Alto', value: 'high' },
];

const normalizeString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
};

const toDisplayText = (value) => {
  const normalized = normalizeString(value);
  return normalized || '-';
};

const normalizeUpper = (value) => normalizeString(value).toUpperCase();

const getApiErrorMessage = (requestError, fallbackMessage) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallbackMessage;
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const formatDate = (value) => {
  const parsed = parseDate(value);
  if (!parsed) {
    return '-';
  }
  return parsed.toLocaleDateString('pt-BR');
};

const getTrialWindow = (trialEndsAt) => {
  const parsed = parseDate(trialEndsAt);
  if (!parsed) {
    return 'none';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const trialDate = new Date(parsed);
  trialDate.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((trialDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'expired';
  }
  if (diffDays <= 7) {
    return 'up_to_7';
  }
  if (diffDays <= 30) {
    return 'up_to_30';
  }
  return 'future';
};

const getRisk = (tenant) => {
  const subscriptionStatus = normalizeUpper(tenant?.subscription_status);
  const tenantStatus = normalizeUpper(tenant?.status);
  const trialWindow = getTrialWindow(tenant?.trial_ends_at);

  if (
    subscriptionStatus === 'SUSPENDED'
    || subscriptionStatus === 'CANCELLED'
    || tenantStatus === 'INACTIVE'
    || tenantStatus === 'SUSPENDED'
    || tenantStatus === 'CANCELLED'
  ) {
    return 'high';
  }

  if (!subscriptionStatus || subscriptionStatus === 'NO_SUBSCRIPTION') {
    return 'attention';
  }

  if (subscriptionStatus === 'TRIAL' && (trialWindow === 'up_to_7' || trialWindow === 'expired')) {
    return 'attention';
  }

  return 'low';
};

const riskLabelMap = {
  low: 'Baixo',
  attention: 'Atencao',
  high: 'Alto',
};

const riskColorMap = {
  low: 'green',
  attention: 'gold',
  high: 'red',
};

const statusColorMap = {
  ACTIVE: 'green',
  TRIAL: 'blue',
  SUSPENDED: 'orange',
  CANCELLED: 'red',
  GRACE: 'gold',
  NO_SUBSCRIPTION: 'default',
};

const PlatformSaasSubscriptionsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState({
    total_tenants: 0,
    active_tenants: 0,
    trial_tenants: 0,
    inactive_tenants: 0,
    tenants_without_subscription: 0,
  });
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({
    subscriptionStatus: undefined,
    planCode: undefined,
    risk: undefined,
    query: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [dashboardResponse, tenantsResponse] = await Promise.all([
        platformSaasSubscriptionsDashboardService.getDashboardMetrics(),
        platformSaasSubscriptionsDashboardService.listTenants(),
      ]);

      const dashboardData = dashboardResponse?.data || {};
      const tenantRows = Array.isArray(tenantsResponse?.data) ? tenantsResponse.data : [];

      setMetrics({
        total_tenants: Number(dashboardData?.total_tenants || 0),
        active_tenants: Number(dashboardData?.active_tenants || 0),
        trial_tenants: Number(dashboardData?.trial_tenants || 0),
        inactive_tenants: Number(dashboardData?.inactive_tenants || 0),
        tenants_without_subscription: Number(dashboardData?.tenants_without_subscription || 0),
      });
      setTenants(tenantRows);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar o dashboard gerencial de assinaturas.'));
      setMetrics({
        total_tenants: 0,
        active_tenants: 0,
        trial_tenants: 0,
        inactive_tenants: 0,
        tenants_without_subscription: 0,
      });
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const planOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        tenants
          .map((tenant) => normalizeString(tenant?.plan_code))
          .filter((value) => value.length > 0)
      )
    );
    unique.sort((a, b) => a.localeCompare(b));
    return unique.map((value) => ({ label: value, value }));
  }, [tenants]);

  const enrichedTenants = useMemo(() => {
    return tenants.map((tenant) => {
      const normalizedSubscriptionStatus = normalizeUpper(tenant?.subscription_status) || 'NO_SUBSCRIPTION';
      const normalizedPlan = normalizeString(tenant?.plan_code) || '-';
      const risk = getRisk(tenant);
      const trialWindow = getTrialWindow(tenant?.trial_ends_at);

      return {
        ...tenant,
        normalizedSubscriptionStatus,
        normalizedPlan,
        risk,
        trialWindow,
      };
    });
  }, [tenants]);

  const filteredTenants = useMemo(() => {
    const normalizedQuery = normalizeUpper(filters.query);

    return enrichedTenants.filter((tenant) => {
      if (filters.subscriptionStatus && tenant.normalizedSubscriptionStatus !== filters.subscriptionStatus) {
        return false;
      }
      if (filters.planCode && tenant.normalizedPlan !== filters.planCode) {
        return false;
      }
      if (filters.risk && tenant.risk !== filters.risk) {
        return false;
      }

      if (normalizedQuery) {
        const name = normalizeUpper(tenant?.name);
        const slug = normalizeUpper(tenant?.slug);
        if (!name.includes(normalizedQuery) && !slug.includes(normalizedQuery)) {
          return false;
        }
      }

      return true;
    });
  }, [enrichedTenants, filters]);

  const inactiveSuspendedCount = useMemo(() => {
    return enrichedTenants.filter((tenant) => {
      const subscriptionStatus = tenant.normalizedSubscriptionStatus;
      const tenantStatus = normalizeUpper(tenant?.status);
      return (
        subscriptionStatus === 'SUSPENDED'
        || subscriptionStatus === 'CANCELLED'
        || tenantStatus === 'INACTIVE'
        || tenantStatus === 'SUSPENDED'
      );
    }).length;
  }, [enrichedTenants]);

  const subscriptionStatusDistribution = useMemo(() => {
    const counts = new Map();
    enrichedTenants.forEach((tenant) => {
      const key = tenant.normalizedSubscriptionStatus;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([subscription_status, total]) => ({ subscription_status, total }))
      .sort((a, b) => b.total - a.total || a.subscription_status.localeCompare(b.subscription_status));
  }, [enrichedTenants]);

  const planDistribution = useMemo(() => {
    const counts = new Map();
    enrichedTenants.forEach((tenant) => {
      counts.set(tenant.normalizedPlan, (counts.get(tenant.normalizedPlan) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([plan_code, total]) => ({ plan_code, total }))
      .sort((a, b) => b.total - a.total || a.plan_code.localeCompare(b.plan_code));
  }, [enrichedTenants]);

  const trialRows = useMemo(() => {
    return enrichedTenants
      .filter((tenant) => tenant.normalizedSubscriptionStatus === 'TRIAL')
      .map((tenant) => {
        let janela = 'Fora da janela';
        if (tenant.trialWindow === 'expired') {
          janela = 'Vencido';
        } else if (tenant.trialWindow === 'up_to_7') {
          janela = 'Ate 7 dias';
        } else if (tenant.trialWindow === 'up_to_30') {
          janela = 'Ate 30 dias';
        }

        return {
          key: tenant.id,
          tenant_name: toDisplayText(tenant?.name),
          slug: toDisplayText(tenant?.slug),
          trial_ends_at: tenant?.trial_ends_at || null,
          trial_window_label: janela,
          trial_window: tenant.trialWindow,
        };
      })
      .sort((a, b) => {
        const dateA = parseDate(a.trial_ends_at);
        const dateB = parseDate(b.trial_ends_at);
        if (!dateA && !dateB) {
          return 0;
        }
        if (!dateA) {
          return 1;
        }
        if (!dateB) {
          return -1;
        }
        return dateA.getTime() - dateB.getTime();
      });
  }, [enrichedTenants]);

  const trialCounters = useMemo(() => {
    let upTo7 = 0;
    let upTo30 = 0;
    let expired = 0;

    trialRows.forEach((row) => {
      if (row.trial_window === 'up_to_7') {
        upTo7 += 1;
      } else if (row.trial_window === 'up_to_30') {
        upTo30 += 1;
      } else if (row.trial_window === 'expired') {
        expired += 1;
      }
    });

    return { upTo7, upTo30, expired };
  }, [trialRows]);

  const portfolioColumns = [
    {
      title: 'Tenant',
      dataIndex: 'name',
      key: 'name',
      render: (value) => toDisplayText(value),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (value) => toDisplayText(value),
    },
    {
      title: 'Plan code',
      key: 'plan_code',
      render: (_, row) => row.normalizedPlan,
    },
    {
      title: 'Subscription status',
      key: 'subscription_status',
      render: (_, row) => (
        <Tag color={statusColorMap[row.normalizedSubscriptionStatus] || 'default'}>
          {row.normalizedSubscriptionStatus}
        </Tag>
      ),
    },
    {
      title: 'Trial ends',
      dataIndex: 'trial_ends_at',
      key: 'trial_ends_at',
      render: (value) => formatDate(value),
    },
    {
      title: 'Status tenant',
      dataIndex: 'status',
      key: 'status',
      render: (value) => toDisplayText(normalizeUpper(value)),
    },
    {
      title: 'Risco',
      key: 'risk',
      render: (_, row) => <Tag color={riskColorMap[row.risk] || 'default'}>{riskLabelMap[row.risk] || '-'}</Tag>,
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, row) => {
        const tenantId = normalizeString(row?.id);
        const canNavigateToDetail = tenantId.length > 0;

        return (
          <Button
            type="link"
            size="small"
            disabled={!canNavigateToDetail}
            onClick={() => {
              if (!canNavigateToDetail) {
                return;
              }
              navigate(`/platform/tenant/${tenantId}`);
            }}
          >
            Ver detalhes
          </Button>
        );
      },
    },
  ];

  const planColumns = [
    {
      title: 'Plan code',
      dataIndex: 'plan_code',
      key: 'plan_code',
    },
    {
      title: 'Tenants',
      dataIndex: 'total',
      key: 'total',
    },
  ];

  const statusColumns = [
    {
      title: 'Subscription status',
      dataIndex: 'subscription_status',
      key: 'subscription_status',
      render: (value) => <Tag color={statusColorMap[value] || 'default'}>{value}</Tag>,
    },
    {
      title: 'Tenants',
      dataIndex: 'total',
      key: 'total',
    },
  ];

  const trialsColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Trial ends',
      dataIndex: 'trial_ends_at',
      key: 'trial_ends_at',
      render: (value) => formatDate(value),
    },
    {
      title: 'Janela',
      dataIndex: 'trial_window_label',
      key: 'trial_window_label',
      render: (_, row) => {
        const color =
          row.trial_window === 'expired'
            ? 'red'
            : row.trial_window === 'up_to_7'
              ? 'gold'
              : row.trial_window === 'up_to_30'
                ? 'blue'
                : 'default';
        return <Tag color={color}>{row.trial_window_label}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Carregando dashboard gerencial de assinaturas..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Assinaturas SaaS - Gestao de Carteira
      </Title>
      <Text type="secondary">
        Visao gerencial somente leitura para acompanhamento da carteira SaaS da plataforma.
      </Text>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Falha ao carregar dashboard gerencial"
          description={error}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16 }}
        message="Provider real permanece desabilitado nesta fase"
        description="Visao gerencial estimada e somente leitura. Nenhuma acao de cobranca/ativacao esta disponivel nesta pagina."
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 12 }}
        message="MRR ainda nao e exibido"
        description="MRR ainda nao e exibido porque depende de consolidacao de preco/custom terms."
      />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card><Statistic title="Total de tenants" value={metrics.total_tenants} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card><Statistic title="Active" value={metrics.active_tenants} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card><Statistic title="Trial" value={metrics.trial_tenants} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card><Statistic title="Suspended/Inactive/Cancelled" value={inactiveSuspendedCount} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card><Statistic title="Sem assinatura" value={metrics.tenants_without_subscription} /></Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="Distribuicao por Plano">
            <Table
              rowKey={(row) => row.plan_code}
              columns={planColumns}
              dataSource={planDistribution}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card size="small" title="Contagem por Subscription Status">
            <Table
              rowKey={(row) => row.subscription_status}
              columns={statusColumns}
              dataSource={subscriptionStatusDistribution}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Trials" style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 12 }} wrap>
          <Tag color="gold">Ate 7 dias: {trialCounters.upTo7}</Tag>
          <Tag color="blue">Ate 30 dias: {trialCounters.upTo30}</Tag>
          <Tag color="red">Vencidos: {trialCounters.expired}</Tag>
        </Space>

        <Table
          rowKey={(row) => row.key}
          columns={trialsColumns}
          dataSource={trialRows}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          size="small"
        />
      </Card>

      <Card size="small" title="Carteira de Tenants" style={{ marginTop: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="Status assinatura"
            value={filters.subscriptionStatus}
            options={SUBSCRIPTION_STATUS_OPTIONS}
            style={{ width: 220 }}
            onChange={(value) => setFilters((prev) => ({ ...prev, subscriptionStatus: value }))}
          />
          <Select
            allowClear
            placeholder="Plan code"
            value={filters.planCode}
            options={planOptions}
            style={{ width: 180 }}
            onChange={(value) => setFilters((prev) => ({ ...prev, planCode: value }))}
          />
          <Select
            allowClear
            placeholder="Risco"
            value={filters.risk}
            options={RISK_OPTIONS}
            style={{ width: 140 }}
            onChange={(value) => setFilters((prev) => ({ ...prev, risk: value }))}
          />
          <Input
            placeholder="Buscar por nome ou slug"
            value={filters.query}
            style={{ width: 260 }}
            onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
          />
        </Space>

        <Table
          rowKey={(row) => row.id}
          columns={portfolioColumns}
          dataSource={filteredTenants}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: 1100 }}
        />
      </Card>
    </div>
  );
};

export default PlatformSaasSubscriptionsDashboard;
